import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { User } from '../types';
import { usersAPI } from '../services/api';

const Container = styled.div`
  background: var(--card-bg);
  border-radius: 15px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
  margin-bottom: 20px;
  color: white;
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserEmail = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const UserRole = styled.div<{ role: string }>`
  font-size: 0.9rem;
  color: ${props => props.role === 'admin' ? 'var(--primary-color)' : 'var(--text-muted)'};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &.role {
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--secondary-color);
    }
  }
  
  &.reset {
    background: var(--warning-color);
    color: white;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

const Modal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--dark-bg);
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  margin: 12px 0;
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

interface UserManagementProps {
  show: boolean;
  onClose: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ show, onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (show) {
      loadUsers();
    }
  }, [show]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleRoleToggle = async (user: User) => {
    const newRole = user.role === 'admin' ? 'listener' : 'admin';
    try {
      await usersAPI.updateRole(user.id, newRole);
      loadUsers();
    } catch (error) {
      console.error('Erro ao alterar role:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    try {
      await usersAPI.resetPassword(selectedUser.id, newPassword);
      setShowResetModal(false);
      setSelectedUser(null);
      setNewPassword('');
      alert('Senha resetada com sucesso!');
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      alert('Erro ao resetar senha');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Tem certeza que deseja deletar o usuário ${user.email}?`)) return;
    
    try {
      await usersAPI.delete(user.id);
      loadUsers();
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      alert('Erro ao deletar usuário');
    }
  };

  if (!show) return null;

  return (
    <>
      <Modal show={show}>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Title>👥 Gerenciar Usuários</Title>
            <button 
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
          
          <UsersList>
            {users.map(user => (
              <UserItem key={user.id}>
                <UserInfo>
                  <UserEmail>{user.email}</UserEmail>
                  <UserRole role={user.role}>
                    {user.role === 'admin' ? '👑 Administrador' : '👤 Ouvinte'}
                  </UserRole>
                </UserInfo>
                
                <Actions>
                  <ActionButton 
                    className="role"
                    onClick={() => handleRoleToggle(user)}
                    title={user.role === 'admin' ? 'Tornar Ouvinte' : 'Tornar Admin'}
                  >
                    {user.role === 'admin' ? '👤' : '👑'}
                  </ActionButton>
                  
                  <ActionButton 
                    className="reset"
                    onClick={() => {
                      setSelectedUser(user);
                      setShowResetModal(true);
                    }}
                    title="Resetar Senha"
                  >
                    🔑
                  </ActionButton>
                  
                  <ActionButton 
                    className="delete"
                    onClick={() => handleDeleteUser(user)}
                    title="Deletar Usuário"
                  >
                    🗑️
                  </ActionButton>
                </Actions>
              </UserItem>
            ))}
          </UsersList>
        </Container>
      </Modal>

      {/* Modal Reset Senha */}
      <Modal show={showResetModal}>
        <ModalContent>
          <h3>🔑 Resetar Senha</h3>
          <p>Usuário: {selectedUser?.email}</p>
          <Input
            type="password"
            placeholder="Nova senha"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button 
              className="btn btn-primary"
              onClick={handleResetPassword}
              disabled={!newPassword}
            >
              Resetar
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setShowResetModal(false);
                setSelectedUser(null);
                setNewPassword('');
              }}
            >
              Cancelar
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserManagement;